import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useHasPermission } from "../contexts/PermissionsContext";
import useModules from "../hooks/useModules";
import { api, formatApiError } from "../lib/api";
import { ROLES, ROLE_LABELS } from "../lib/rbac";
import { Button } from "../components/ui/button";
import {
    LogOut, ArrowRight, ClipboardCheck, UserPlus, DollarSign, CalendarPlus,
    Warehouse, Building2, HardHat, Building, Wallet, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import HeaderTools from "../components/HeaderTools";
import EmptyState from "../components/EmptyState";
import EarlyDeparturesWeekTile from "../components/EarlyDeparturesWeekTile";
import EarlyDeparturesDrillModal from "../components/EarlyDeparturesDrillModal";
import IncidentsKpiTile from "../components/IncidentsKpiTile";
import {
    WeeklyBarsTile, PayrollRingTile, ProjectedCostTile, ProjectedCostModal,
    AlertsMiniTile, RecentActivityTile, QuickAddTile, WeeklyCostModal,
    MiniKPI, fmtTodayLong, fmtUSD0, todayIso,
} from "../components/BentoTiles";

/**
 * Iter59.p20 — Landing dashboard.
 *
 * Same Bento Grid layout as the module workspace dashboard, but with
 * CROSS-MODULE aggregation. Data comes from `/api/dashboard/home`
 * (already returns aggregated KPIs / trend / cost-by-module / recent
 * activity across every module the user has access to) plus the most
 * recent payroll period from `/api/payroll/periods`.
 *
 * Tiles are the shared `<BentoTiles>` components — no duplicate code
 * with `ModuleDashboard.jsx`.
 */

// ---------------------------------------------------------------------------
// Iter78 — Chain OK badge (Admin only). Polls /api/admin/audit/verify-chain
// every 5 minutes; green pill when clean, crimson pill on tamper. Clicking
// takes the admin straight to the audit console.
// ---------------------------------------------------------------------------
function ChainOkBadge() {
    const navigate = useNavigate();
    const [state, setState] = useState({ ok: null, verified_count: 0, tamper_at: null, at: null, acknowledged_seqs: [] });
    const verify = async () => {
        try {
            const r = await api.get("/admin/audit/verify-chain?max_entries=1000");
            setState({
                ok: !!r.data.ok,
                verified_count: r.data.verified_count || 0,
                tamper_at: r.data.tamper_at,
                acknowledged_seqs: r.data.acknowledged_seqs || [],
                at: new Date(),
            });
        } catch { /* keep last known — silent */ }
    };
    useEffect(() => {
        verify();
        const t = setInterval(verify, 5 * 60_000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (state.ok === null) return null;
    if (state.ok) {
        const ackCount = state.acknowledged_seqs?.length || 0;
        const hasQuarantined = ackCount > 0;
        return (
            <button
                onClick={() => navigate("/admin/audit")}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs hover:bg-emerald-100 transition"
                title={
                    hasQuarantined
                        ? `Verified ${state.verified_count} entries · ${ackCount} quarantined (seq${ackCount === 1 ? "" : "s"} ${state.acknowledged_seqs.join(", ")})`
                        : `Verified ${state.verified_count} entries · click for details`
                }
                data-testid={hasQuarantined ? "home-chain-ok-badge-quarantined" : "home-chain-ok-badge"}
            >
                <span className="relative inline-flex">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    {hasQuarantined && (
                        <span
                            className="absolute -top-0.5 -right-1 w-1.5 h-1.5 rounded-full bg-amber-500 ring-1 ring-white"
                            aria-hidden="true"
                        />
                    )}
                </span>
                <span className="font-medium">
                    {hasQuarantined
                        ? `Chain OK · ${ackCount}Q`
                        : "Chain OK"}
                </span>
            </button>
        );
    }
    return (
        <button
            onClick={() => navigate("/admin/audit")}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-50 border border-rose-300 text-rose-800 text-xs hover:bg-rose-100 transition animate-pulse"
            title={`Tamper at seq #${state.tamper_at} — investigate immediately`}
            data-testid="home-chain-tamper-badge"
        >
            <ShieldAlert className="h-3.5 w-3.5" />
            <span className="font-medium">Tamper @ #{state.tamper_at}</span>
        </button>
    );
}

export default function Home() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const { modules } = useModules();
    const canReviewDiscipline = useHasPermission("discipline.review", null);
    const [data, setData] = useState(null);
    const [period, setPeriod] = useState(null);
    const [master, setMaster] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pendingReviews, setPendingReviews] = useState(null);
    // Iter60.p31 — Weekly Cost read-only breakdown modal state
    const [weeklyOpen, setWeeklyOpen] = useState(false);
    const [weeklyData, setWeeklyData] = useState(null);
    const [weeklyLoading, setWeeklyLoading] = useState(false);
    const [weeklyModuleLabel, setWeeklyModuleLabel] = useState(null);
    // Iter60.p38 — Projected Cost breakdown modal state (Home).
    const [projectedOpen, setProjectedOpen] = useState(false);
    const [projectedContributors, setProjectedContributors] = useState([]);
    // Iter60.p43.3 — Early Departures This Week tile + drill modal.
    const [earlyDep, setEarlyDep] = useState(null);
    const [earlyDepLoading, setEarlyDepLoading] = useState(true);
    const [earlyDepOpen, setEarlyDepOpen] = useState(false);

    const isDeptMgr = user?.role === ROLES.ASL_DEPT_MGR;
    // Iter60.p48 — Attendance Officer is a cross-module writer BUT is
    // explicitly blocked from every finance/payroll surface. Suppress
    // the Master Finance card + the projected/weekly cost tiles they
    // would otherwise inherit from the "all readers" branch.
    const isAttOfficer = user?.role === ROLES.ASL_ATTENDANCE_OFFICER;
    // Iter60.p50.2 — Attendance Officer NOW stays on Home (previously
    // we auto-redirected them to `/m/cn/attendance` which produced a
    // dead-end 403 on production when the seeded `module_scope` UUID
    // drifted from the CN module id). Home renders a single-tile
    // Construction card that routes them straight into
    // `/m/cn/attendance` — one intentional click, one code-based gate
    // check, no UUID drift trap.
    /* Iter59.p22 — Restore prod's canSeeMaster gate: everyone EXCEPT dept
       managers can see the Consolidated · Master Finance card.
       Iter60.p48 — Also hide it from Attendance Officer. */
    const canSeeMaster = !isDeptMgr && !isAttOfficer;

    // Toast handoff from legacy redirects.
    const toastFiredRef = useRef(false);
    useEffect(() => {
        const msg = location.state?.toast;
        if (!msg || toastFiredRef.current) return;
        toastFiredRef.current = true;
        const kind = location.state?.toastKind || "info";
        if (kind === "error") toast.error(msg); else toast.info(msg);
        navigate(location.pathname, { replace: true, state: {} });
    }, [location.state?.toast, location.state?.toastKind, location.pathname, navigate]);

    // Single aggregation fetch + soonest payroll period + master overview.
    useEffect(() => {
        setLoading(true);
        const tasks = [
            api.get("/dashboard/home"),
        ];
        // Iter60.p48 — Attendance Officer has no payroll.view. Skip the
        // payroll period fetch (would 403) and downstream ring tile.
        if (!isAttOfficer) tasks.push(api.get("/payroll/periods?page_size=1&sort=-created_at"));
        else tasks.push(Promise.resolve({ data: { items: [] } }));
        // Iter59.p22 — Master Finance aggregate for the Consolidated card.
        // Skip the call entirely for dept managers (would 403 anyway).
        if (canSeeMaster) tasks.push(api.get("/dashboard/master-overview"));
        Promise.all(tasks)
            .then(([d1, d2, d3]) => {
                // Iter60.p31 followup — Overlay the accurate
                // `weekly_cost_total` (actual attended cost across
                // visible modules) into `kpis.week_cost.value` so the
                // Weekly Cost KPI matches the read-only modal.
                // Backend field lands empty for legacy clients; skip
                // when absent.
                const home = d1.data || {};
                if (
                    home.weekly_cost_total !== undefined
                    && home.weekly_cost_total !== null
                    && home.kpis
                ) {
                    home.kpis = {
                        ...home.kpis,
                        week_cost: {
                            ...(home.kpis.week_cost || {}),
                            value: home.weekly_cost_total,
                        },
                    };
                }
                setData(home);
                setPeriod((d2.data?.items || [])[0] || null);
                if (d3) setMaster(d3.data);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [canSeeMaster]);

    // Pending Kaafi discipline reviews KPI (only for users with the
    // discipline.review permission).
    useEffect(() => {
        if (!canReviewDiscipline) { setPendingReviews(null); return; }
        api.get("/discipline", { params: { status: "awaiting_review", page_size: 1 } })
            .then(({ data }) => setPendingReviews(data.total ?? (data.items || []).length))
            .catch(() => setPendingReviews(0));
    }, [canReviewDiscipline]);

    // Iter60.p43.3 — Load the "Early Departures This Week" aggregation.
    // Fires alongside the main `/dashboard/home` fetch; dept-manager
    // scoping is handled server-side.
    useEffect(() => {
        let cancelled = false;
        setEarlyDepLoading(true);
        api.get("/dashboard/early-departures/week")
            .then(({ data }) => {
                if (!cancelled) setEarlyDep(data);
            })
            .catch((e) => {
                if (!cancelled) {
                    toast.error(formatApiError(e));
                    setEarlyDep(null);
                }
            })
            .finally(() => {
                if (!cancelled) setEarlyDepLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const handleLogout = async () => { await logout(); navigate("/login", { replace: true }); };

    const kpis = data?.kpis;
    const trend = data?.attendance_trend || [];
    const last7 = trend.slice(-7);
    const costByModule = data?.cost_by_module || [];
    const actions = data?.recent_actions || [];

    /* Iter59.p20 — Cost trend synthesized from attendance_trend using the
       aggregated month cost across all accessible modules as the scale
       factor. Same shape as the module dashboard so the tile is
       comparable.  */
    const monthTotalCost = costByModule.reduce((s, r) => s + (r.cost || 0), 0);
    const costTrendData = useMemo(() => {
        const totalActivity = Math.max(1, trend.reduce(
            (s, r) => s + (r.present || 0) + (r.late || 0), 0,
        ));
        const scale = monthTotalCost / totalActivity;
        return trend.map((r) => ({
            date: r.date,
            cost: Math.round(((r.present || 0) + (r.late || 0)) * scale),
        }));
    }, [trend, monthTotalCost]);
    const totalCostTrend = costTrendData.reduce((s, r) => s + r.cost, 0);

    /* Iter59.p20 — Welcome heading uses the logged-in user's name (or
       their role label) instead of just the module short code. */
    const welcomeHeading = user?.full_name || user?.name
        || (user?.role ? (ROLE_LABELS[user.role] || user.role) : "Welcome back");
    const roleLabel = user?.role ? (ROLE_LABELS[user.role] || user.role) : "";
    const scopeSubtitle = isDeptMgr && data?.scope?.module
        ? data.scope.module.name
        : "All modules";

    /* Payroll ring — show the module code chip for context, since this
       period may be from any module. */
    const payrollModuleChip = period
        ? (modules.find((m) => m.id === period.module_id)?.code || "—")
        : null;

    /* Alerts — cross-module counters. `pendingReviews` only meaningful
       for reviewers, so we only include the row when it's non-null. */
    const openExceptions = kpis?.exceptions_open?.value || 0;
    const reviewsCount = pendingReviews || 0;
    const alertsCount = openExceptions + reviewsCount;
    const alertsBreakdown = [
        { label: "Open exceptions", value: openExceptions, link: modules.length === 1
            ? `/m/${modules[0].code.toLowerCase()}/attendance/exceptions`
            : "/" },
    ];
    if (canReviewDiscipline) {
        const modCode = (user?.module_scope_code || modules?.[0]?.code || "AP").toLowerCase();
        alertsBreakdown.push({
            label: "Pending discipline reviews",
            value: reviewsCount,
            link: `/m/${modCode}/discipline?status=awaiting_review`,
        });
    }

    /* Iter59.p21 — Rich per-module cards for the top-of-page hero.
       Backend already returns `active_employees` and `exceptions_open`
       on each `cost_by_module` row so we don't need extra fetches. */
    // Dept managers only see their scoped module.
    // Iter60.p50.2 — Attendance Officer scoped to their single module
    // too, so the Home cards grid renders just one Construction tile
    // they can actually click into.
    // Iter60.p50.3 — Case-insensitive code match + role-based CN
    // fallback for the Officer. Fixes production dead-end where the
    // stored `module_scope` UUID had drifted AND `module_scope_code`
    // was empty on the row — read-time resolution in
    // `get_current_user` now normally populates both, but the
    // frontend keeps a defensive fallback so a stale cached `me`
    // response can't hide the Construction tile.
    const officerScopeMatches = (m) => {
        const stored = (user?.module_scope_code || "").toUpperCase();
        const mc = (m?.code || "").toUpperCase();
        if (m.id === user?.module_scope) return true;
        if (stored && mc === stored) return true;
        // Attendance Officer is defined as CN-only. If both scope
        // fields are missing, still render the Construction tile.
        if (isAttOfficer && !stored && !user?.module_scope && mc === "CN") return true;
        return false;
    };
    const visibleModules = (isDeptMgr || isAttOfficer)
        ? modules.filter(officerScopeMatches)
        : modules;
    const moduleHeroCards = useMemo(() => {
        const withStats = costByModule
            .filter((r) => visibleModules.find((m) => m.id === r.module_id))
            .map((r) => ({
                id: r.module_id,
                code: r.code,
                name: r.name || r.code,
                cost: r.cost || 0,
                employees: r.active_employees ?? null,
                exceptions: r.exceptions_open ?? null,
            }));
        // Include any accessible module NOT in cost_by_module (edge case:
        // brand-new module with no attendance yet).
        for (const m of visibleModules) {
            if (!withStats.find((r) => r.id === m.id)) {
                withStats.push({ id: m.id, code: m.code, name: m.name, cost: 0, employees: null, exceptions: null });
            }
        }
        return withStats;
    }, [costByModule, visibleModules]);

    /* Quick add — first module the user can navigate to (dept mgr scoped
       to theirs; others default to first accessible). Falls back safely
       when no modules are loaded yet. */
    const quickBase = visibleModules.length > 0
        ? `/m/${visibleModules[0].code.toLowerCase()}`
        : null;
    // Iter60.p48.2 — Filter Quick Add tiles by the caller's ROLE_DEFAULTS.
    // Each tile carries the `perm` key that its landing page requires;
    // if the role lacks it, the tile is hidden. Prevents Attendance
    // Officer from clicking into a tile that would 403 (they'd land on
    // the p48.1 fallback card, but the tile itself should never render).
    const quickAddActions = quickBase ? [
        { icon: ClipboardCheck, label: "Attendance", to: `${quickBase}/attendance`, perm: "attendance.view" },
        { icon: UserPlus, label: "Employee", to: `${quickBase}/staff`, perm: "employees.view" },
        { icon: DollarSign, label: "Wage Rate", to: `${quickBase}/wage-rates`, perm: "wage_rates.view" },
        { icon: CalendarPlus, label: "Payroll", to: `${quickBase}/payroll`, perm: "payroll.view" },
    ] : [];

    // Iter60.p48.2 — Resolve perms for each Quick Add tile upfront so
    // the render filter is a plain array `.filter()` (hooks can't run
    // inside a map callback conditionally). Roles that lack any of
    // these permissions see fewer tiles — Attendance Officer, for
    // example, only sees Attendance + Employee.
    const canQuickAttendance = useHasPermission("attendance.view", null);
    const canQuickEmployee = useHasPermission("employees.view", null);
    const canQuickWageRate = useHasPermission("wage_rates.view", null);
    const canQuickPayroll = useHasPermission("payroll.view", null);
    const quickAddActionsFiltered = quickAddActions.filter((a) => {
        if (a.perm === "attendance.view") return canQuickAttendance;
        if (a.perm === "employees.view") return canQuickEmployee;
        if (a.perm === "wage_rates.view") return canQuickWageRate;
        if (a.perm === "payroll.view") return canQuickPayroll;
        return true;
    });

    // Iter60.p31 — open the Weekly Cost read-only modal. For the Home
    // KPI the scope covers ALL accessible modules; we default the
    // fetch to the largest-cost module (best proxy for "the weekly
    // number the user just clicked"). Dept managers see their scope
    // only, so it lands on the correct module by construction.
    const openWeeklyModal = async () => {
        const modId = moduleHeroCards[0]?.id
            ?? visibleModules[0]?.id
            ?? null;
        if (!modId) return;
        const modLabel = (moduleHeroCards[0]?.name || visibleModules[0]?.name) || "";
        setWeeklyModuleLabel(modLabel);
        setWeeklyOpen(true);
        setWeeklyLoading(true);
        try {
            const { data: wc } = await api.get(
                `/dashboard/module/${modId}/weekly-cost`
            );
            setWeeklyData(wc);
        } catch (e) {
            toast.error(formatApiError(e));
            setWeeklyData(null);
        } finally {
            setWeeklyLoading(false);
        }
    };

    return (
        <div className="min-h-screen" style={{ background: "var(--asl-bg-2)" }} data-testid="home-page">
            <header
                className="px-8 py-4 flex items-center justify-between"
                style={{ background: "var(--asl-surface)", borderBottom: "1px solid var(--asl-divider)" }}
            >
                <div className="flex items-center gap-3">
                    <img src="/asl-logo.png" alt="ASL" style={{ height: 40 }} className="asl-logo--washed" data-testid="home-logo" />
                    <div>
                        <div className="text-card-title" style={{ color: "var(--asl-navy-2)" }}>ASL HR Management Platform</div>
                        <div className="text-xs" style={{ color: "var(--asl-text-3)" }}>Managed by Kaafi Hire Employment Services Ltd</div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <HeaderTools />
                    <div className="text-right text-xs">
                        <div className="font-semibold" style={{ color: "var(--asl-text)" }}>{user?.name}</div>
                        <div style={{ color: "var(--asl-text-3)" }}>{roleLabel}</div>
                    </div>
                    {user?.role === ROLES.KAAFI_ADMIN && (
                        <>
                            <ChainOkBadge />
                            <Button variant="outline" size="sm" onClick={() => navigate("/admin/users")} data-testid="home-users">Users</Button>
                            <Button variant="outline" size="sm" onClick={() => navigate("/admin/settings")} data-testid="home-settings">Settings</Button>
                            <Button variant="outline" size="sm" onClick={() => navigate("/admin/audit")} data-testid="home-audit">Audit</Button>
                        </>
                    )}
                    <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button">
                        <LogOut className="h-4 w-4 mr-1" /> Logout
                    </Button>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-8 py-8" data-testid="home-main">
                <section className="asl-bento" data-testid="home-bento">
                    <div className="asl-bento-grid asl-bento-grid--landing">
                        <WelcomeBar
                            heading={welcomeHeading}
                            subheading={`${fmtTodayLong()} · ${scopeSubtitle}`}
                            rightMessage={
                                canReviewDiscipline && reviewsCount > 0
                                    ? `You have ${reviewsCount} pending review${reviewsCount === 1 ? "" : "s"}`
                                    : null
                            }
                            onRightClick={canReviewDiscipline && quickBase
                                ? () => navigate(`${quickBase}/discipline?status=awaiting_review`)
                                : null}
                        />
                        <ModulesHeroTile
                            cards={moduleHeroCards}
                            currentModuleId={isDeptMgr ? user?.module_scope : null}
                            loading={loading}
                            /* Iter60.p50.2 — Attendance Officer can't open Module
                               Dashboard (blocked in ModuleAppShell), so send them
                               straight into `/m/{code}/attendance` from the card.
                               Everyone else still lands on the module dashboard. */
                            onCardClick={(code) => navigate(
                                isAttOfficer
                                    ? `/m/${code.toLowerCase()}/attendance`
                                    : `/m/${code.toLowerCase()}`
                            )}
                            /* Iter60.p50.2 — Hide the `Month Cost` stat for the
                               Attendance Officer (p50 spec: no finance/cost
                               visible anywhere). */
                            hideCost={isAttOfficer}
                            master={canSeeMaster ? master : null}
                            /* Iter60.p40 Item A — Master Finance card MONTH COST now
                               reads the unified MTD helper (same math as each
                               module card), guaranteeing the consolidated value
                               equals IC + AP + CN by construction. */
                            masterMonthCost={canSeeMaster ? (data?.master_month_cost ?? 0) : 0}
                            onMasterClick={() => navigate("/master-finance")}
                            style={{ gridRow: "2" }}
                        />
                        {/* Iter59.p23 — KPIs live in their own compact row now
                            (they used to sit inside the Welcome tile).
                            Iter60.p50.2 — Suppress the `weekly` (Week Cost)
                            KPI + `weekly` click for the Attendance Officer;
                            they have no finance visibility. */}
                        <KpiRow
                            kpis={isAttOfficer ? { ...kpis, week_cost: null } : kpis}
                            loading={loading}
                            hideWeekly={isAttOfficer}
                            onKpiClick={{
                                active: () => navigate(quickBase ? `${quickBase}/staff?status=active` : "/"),
                                // Iter60.p30 (Task D) — Present Today drills to today's attendance.
                                present: () => navigate(quickBase ? `${quickBase}/attendance?date=${todayIso()}&status=present` : "/"),
                                // Iter60.p31 — Weekly Cost opens a
                                // READ-ONLY breakdown modal (per-day +
                                // top contributors). Was navigating to
                                // the payroll period detail. Scope
                                // heuristic: if a scoped module exists,
                                // fetch that module's breakdown;
                                // otherwise show the newest period's
                                // module.
                                weekly: () => openWeeklyModal(),
                                exceptions: () => navigate(quickBase ? `${quickBase}/attendance/exceptions` : "/"),
                            }}
                        />
                        {/* Iter60.p50.2 — WeeklyBarsTile shows daily cost bars.
                            Suppress for Attendance Officer. */}
                        {!isAttOfficer && (
                            <WeeklyBarsTile last7={last7} loading={loading}
                                onClick={quickBase ? () => navigate(`${quickBase}/attendance`) : undefined}
                                style={{ gridColumn: "9 / span 4", gridRow: "4 / span 2" }} />
                        )}
                        {/* Iter60.p48 — Payroll ring hidden for Attendance Officer. */}
                        {!isAttOfficer && (
                            <PayrollRingTile
                                period={period}
                                moduleChip={payrollModuleChip}
                                loading={loading}
                                onClick={() => {
                                    if (!period) return;
                                    const modCode = modules.find((m) => m.id === period.module_id)?.code?.toLowerCase();
                                    if (modCode) navigate(`/m/${modCode}/payroll/${period.id}`);
                                }}
                                style={{ gridColumn: "1 / span 4", gridRow: "4" }}
                            />
                        )}
                        <ProjectedCostTile
                            data={data?.projected_cost || null}
                            loading={loading}
                            moduleId={null}
                            onShowBreakdown={() => setProjectedOpen(true)}
                            hidden={isAttOfficer}
                            style={{ gridColumn: isAttOfficer ? "1 / span 4" : "5 / span 4", gridRow: "4" }}
                        />
                        <AlertsMiniTile
                            count={alertsCount}
                            breakdown={alertsBreakdown}
                            loading={loading}
                            onNav={(l) => navigate(l)}
                            style={{ gridColumn: "1 / span 3", gridRow: "5" }}
                        />
                        <RecentActivityTile
                            items={actions}
                            loading={loading}
                            emptyMessage="Cross-module actions will surface here."
                            style={{ gridColumn: "4 / span 5", gridRow: "5" }}
                        />
                        <QuickAddTile
                            actions={quickAddActionsFiltered}
                            onNav={(t) => navigate(t)}
                            style={{ gridColumn: "9 / span 4", gridRow: "5" }}
                        />
                        {/* Iter60.p43.3 — Early Departures This Week
                            widget. Full-width tile below the main
                            landing bento so it doesn't crowd the
                            KPI + Weekly bars visual rhythm. */}
                        <EarlyDeparturesWeekTile
                            data={earlyDep}
                            loading={earlyDepLoading}
                            onOpen={() => setEarlyDepOpen(true)}
                        />
                        {/* Iter60.p55b — Incidents & Emergencies KPI tile.
                            Renders for anyone with incidents.view (HR /
                            Admin cross-module aggregate — Manager gets
                            the module-scoped variant on Module
                            Dashboard). Full-width, spans 12 cols. */}
                        {!isAttOfficer && (
                            <IncidentsKpiTile
                                style={{ gridColumn: "1 / span 12", gridRow: "7" }}
                            />
                        )}
                    </div>
                </section>
            </main>

            <WeeklyCostModal
                open={weeklyOpen}
                onClose={() => setWeeklyOpen(false)}
                data={weeklyData}
                loading={weeklyLoading}
            />
            {/* Iter60.p38 — Projected Cost breakdown modal (Home).
                Cross-module aggregation is presented as a per-module
                table so leadership can drill from the summed KPI down
                to the specific module driving the number. */}
            <ProjectedCostModal
                open={projectedOpen}
                onClose={() => setProjectedOpen(false)}
                data={data?.projected_cost || null}
                contributors={projectedContributors}
            />
            {/* Iter60.p43.3 — Early Departures drill-down modal. */}
            <EarlyDeparturesDrillModal
                open={earlyDepOpen}
                onClose={() => setEarlyDepOpen(false)}
                data={earlyDep}
                loading={earlyDepLoading}
            />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Iter59.p21 — Modules Hero (landing-only, top of page)              */
/* ------------------------------------------------------------------ */

const MODULE_ICON_MAP = {
    IC: Warehouse,
    AP: Building2,
    CN: HardHat,
};

function ModulesHeroTile({ cards, currentModuleId, loading, onCardClick, hideCost, master, masterMonthCost, onMasterClick, style }) {
    const showMaster = !!master;
    const accessibleCount = cards.length + (showMaster ? 1 : 0);
    return (
        <div
            className="asl-bento-tile asl-modules-hero"
            data-testid="home-tile-modules-hero"
            style={{ gridColumn: "1 / span 12", gridRow: "1", ...style }}
        >
            <div className="flex items-baseline justify-between mb-3">
                <div>
                    <div className="text-label" style={{ color: "var(--asl-crimson-2)" }}>
                        MODULES
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--asl-text-3)" }}>
                        Click any module to enter its workspace
                    </div>
                </div>
                <div className="text-xs mono" style={{ color: "var(--asl-text-3)" }}>
                    {loading ? "…" : `${accessibleCount} accessible`}
                </div>
            </div>
            {loading ? (
                <div className="asl-modules-hero__row">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="asl-module-card asl-shimmer" style={{ minHeight: 120 }} />
                    ))}
                </div>
            ) : cards.length === 0 && !showMaster ? (
                <EmptyState
                    icon={Building}
                    title="No modules accessible"
                    description="Ask your admin to grant module access."
                />
            ) : (
                <div className="asl-modules-hero__row">
                    {cards.map((c) => {
                        const Icon = MODULE_ICON_MAP[c.code] || Building;
                        const isCurrent = currentModuleId && c.id === currentModuleId;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => onCardClick(c.code)}
                                className="asl-module-card"
                                data-testid={`home-module-card-${c.code}`}
                            >
                                {isCurrent && (
                                    <span className="asl-module-card__current" data-testid={`home-module-current-${c.code}`}>
                                        Current
                                    </span>
                                )}
                                <div className="asl-module-card__top">
                                    <div className="asl-module-card__icon">
                                        <Icon className="h-6 w-6" aria-hidden="true" />
                                    </div>
                                    <div className="asl-module-card__title">
                                        <span className="asl-module-card__code">{c.code}</span>
                                        <span className="asl-module-card__name">{c.name}</span>
                                    </div>
                                    <ArrowRight
                                        className="h-4 w-4 asl-module-card__chevron"
                                        aria-hidden="true"
                                    />
                                </div>
                                <div className="asl-module-card__stats">
                                    <ModuleStat label="Employees" value={c.employees ?? "—"} />
                                    {hideCost ? (
                                        <ModuleStat label="Attendance" value="Open →" accent data-testid={`home-module-card-attendance-${c.code}`} />
                                    ) : (
                                        <ModuleStat label="Month Cost" value={c.cost > 0 ? fmtUSD0(c.cost) : "—"} accent />
                                    )}
                                    <ModuleStat label="Exceptions" value={c.exceptions ?? "—"}
                                        tone={c.exceptions > 0 ? "danger" : "default"} />
                                </div>
                            </button>
                        );
                    })}
                    {showMaster && (
                        <ConsolidatedCard
                            master={master}
                            masterMonthCost={masterMonthCost}
                            moduleCount={cards.length}
                            onClick={onMasterClick}
                        />
                    )}
                </div>
            )}
        </div>
    );
}

/* Iter59.p22 — Consolidated · Master Finance card (dark navy variant).
   Aggregates across every accessible module. Gated to non-dept-managers
   (matches the prod-era `canSeeMaster` rule). Clicking navigates to the
   existing `/master-finance` workspace. */
function ConsolidatedCard({ master, masterMonthCost, moduleCount, onClick }) {
    const workers = master?.kpis?.total_active_workers ?? "—";
    // Iter60.p40 Item A — MONTH COST is now the unified MTD actual
    // attended cost (same population as each module card). Previous
    // source `master.kpis.total_net_payable` measured a completely
    // different thing (finalized payroll net for periods whose
    // start_date falls in the current calendar month), which routinely
    // drifted by an order of magnitude from IC + AP + CN. Prefers the
    // top-level field on `/dashboard/home` (`master_month_cost`) and
    // falls back to the equivalent field on `/dashboard/master-overview`
    // (`mtd_actual_cost`) so either endpoint keeps the card in sync.
    const monthCost = (masterMonthCost != null && masterMonthCost > 0)
        ? masterMonthCost
        : (master?.mtd_actual_cost ?? 0);
    return (
        <button
            type="button"
            onClick={onClick}
            className="asl-module-card asl-module-card--consolidated"
            data-testid="home-module-card-consolidated"
        >
            <div className="asl-module-card__top">
                <div className="asl-module-card__icon asl-module-card__icon--onDark">
                    <Wallet className="h-6 w-6" aria-hidden="true" />
                </div>
                <div className="asl-module-card__title">
                    <span className="asl-module-card__code asl-module-card__code--onDark">CONSOLIDATED</span>
                    <span className="asl-module-card__name asl-module-card__name--onDark">Master Finance</span>
                </div>
                <ArrowRight
                    className="h-4 w-4 asl-module-card__chevron asl-module-card__chevron--onDark"
                    aria-hidden="true"
                />
            </div>
            <div className="asl-module-card__stats asl-module-card__stats--onDark">
                <ModuleStat label="Employees" value={workers} dark />
                <ModuleStat label="Month Cost" value={monthCost > 0 ? fmtUSD0(monthCost) : "—"} dark accent />
                <ModuleStat label="Modules" value={moduleCount} dark />
            </div>
        </button>
    );
}

function ModuleStat({ label, value, accent, tone, dark }) {
    const color = tone === "danger"
        ? (dark ? "#FFB4C0" : "var(--status-error)")
        : accent
            ? (dark ? "#FFB4C0" : "var(--asl-crimson)")
            : (dark ? "#ffffff" : "var(--kpi-number-color)");
    const labelClass = dark ? "text-label asl-module-card__label--onDark" : "text-label";
    return (
        <div className="asl-module-card__stat">
            <div className={labelClass}>{label}</div>
            <div className="mono font-semibold text-sm" style={{ color }}>{value}</div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Iter59.p23 — Compact WelcomeBar (Row 1)                            */
/* ------------------------------------------------------------------ */
/* A slim strip at the very top of the landing dashboard. Contains
   just heading + subheading on the left and an optional right-side
   pending-reviews CTA. The KPIs that used to live inside the old
   WelcomeTile have been extracted into their own <KpiRow /> (Row 3). */
function WelcomeBar({ heading, subheading, rightMessage, onRightClick }) {
    return (
        <div
            className="asl-welcome-bar"
            data-testid="home-tile-welcome-bar"
            style={{ gridColumn: "1 / span 12", gridRow: "1" }}
        >
            <div className="asl-welcome-bar__left">
                <div className="text-label" style={{ color: "var(--asl-crimson-2)" }}>WELCOME BACK</div>
                <div className="asl-welcome-bar__heading" data-testid="home-welcome-heading">{heading}</div>
                <div className="asl-welcome-bar__sub">{subheading}</div>
            </div>
            {rightMessage && (
                <button
                    type="button"
                    onClick={onRightClick || undefined}
                    className="asl-welcome-bar__cta"
                    data-testid="home-welcome-cta"
                    disabled={!onRightClick}
                >
                    {rightMessage}
                    {onRightClick && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
                </button>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* Iter59.p23 — KpiRow (Row 3)                                        */
/* ------------------------------------------------------------------ */
/* Four cross-module KPI mini-tiles in their own dedicated row, using
   the shared MiniKPI component from BentoTiles for visual parity
   with the module workspace dashboard. */
function KpiRow({ kpis, loading, onKpiClick, hideWeekly }) {
    return (
        <div
            className="asl-bento-tile asl-kpi-row"
            data-testid="home-tile-kpis"
            style={{ gridColumn: "1 / span 12", gridRow: "3" }}
        >
            <MiniKPI label="Active Workers" value={kpis?.active_employees?.value ?? "—"}
                loading={loading} onClick={onKpiClick?.active} testid="home-kpi-active" />
            <MiniKPI label="Present Today" value={kpis?.present_today?.value ?? "—"}
                loading={loading} onClick={onKpiClick?.present} testid="home-kpi-present" />
            {!hideWeekly && (
                <MiniKPI label="Weekly Cost" value={fmtUSD0(kpis?.week_cost?.value ?? 0)}
                    loading={loading} onClick={onKpiClick?.weekly} accent testid="home-kpi-weekly" />
            )}
            <MiniKPI label="Open Exceptions" value={kpis?.exceptions_open?.value ?? 0}
                loading={loading} onClick={onKpiClick?.exceptions}
                tone={(kpis?.exceptions_open?.value ?? 0) > 0 ? "danger" : "default"}
                testid="home-kpi-exceptions" />
        </div>
    );
}

